import type { AudioPreset } from './audio-presets';
import { clonePreset, DEFAULT_PRESET } from './audio-presets';
import { releaseHissEnvelope, releaseHissHitForThrottleDrop } from './audio-signals';

// Live driving signals from the physics layer (or the tuner's mock physics).
export interface PhysicsSignals {
  throttle: number;   // 0–1
  brake: number;      // 0–1
  speed: number;      // 0–1, normalized
  slip: number;       // 0–1
  load: number;       // 0–1, derived (throttle * f(speed))
  onTrack: boolean;
  boost?: number;     // 0–1, optional boost button/state for SFX triggers
  boostCharge?: number; // 0–1, remaining boost fuel (driftCharge)
  airBrake?: number;  // 0–1, optional held air-brake state for SFX triggers
}

const WORKLET_URL = '/audio/engine-worklet.js';

interface PodGraph {
  node: AudioWorkletNode;
  pan: StereoPannerNode;
}

// Main-thread façade for the engine synth. Owns the AudioContext, two
// AudioWorkletNode pods, panning, and the physics→param mapping.
//
// One instance per game; in the tuner we instantiate it directly.
export class EngineSynth {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private comp: DynamicsCompressorNode | null = null;
  private pods: PodGraph[] = [];
  private preset: AudioPreset = clonePreset(DEFAULT_PRESET);
  private started = false;
  private suspended = false;

  // Smoothed signals — the audio thread reads from these via setTargetAtTime.
  private smoothedRpm = 0;
  private smoothedThrottle = 0;
  private smoothedLoad = 0;
  private smoothedSlip = 0;
  private smoothedShift = 0;
  private releaseHiss = 0;
  private lastThrottleInput = 0;
  private lastDrive = 0;

  async start(opts: { context?: AudioContext; output?: AudioNode } = {}): Promise<void> {
    if (this.started) return;
    if (opts.context) {
      this.ctx = opts.context;
    } else {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
    }
    await this.ctx.audioWorklet.addModule(WORKLET_URL);

    this.master = this.ctx.createGain();
    this.master.gain.value = 1;

    if (opts.output) {
      // External routing — caller (e.g. GameAudio) owns the master compressor
      // and final destination. Tuner page uses the standalone branch below.
      this.master.connect(opts.output);
    } else {
      this.comp = this.ctx.createDynamicsCompressor();
      this.comp.threshold.value = -10;
      this.comp.knee.value = 6;
      this.comp.ratio.value = 4;
      this.comp.attack.value = 0.003;
      this.comp.release.value = 0.12;
      this.master.connect(this.comp).connect(this.ctx.destination);
    }

    // Two pods, panned L/R via the twin.stereoWidth knob.
    const podL = this.makePod('left');
    const podR = this.makePod('right');
    this.pods = [podL, podR];
    this.applyPreset(this.preset);
    this.started = true;
  }

  private makePod(side: 'left' | 'right'): PodGraph {
    if (!this.ctx || !this.master) throw new Error('AudioContext not initialized');
    const node = new AudioWorkletNode(this.ctx, 'engine-pod', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    const pan = this.ctx.createStereoPanner();
    pan.pan.value = side === 'left' ? -0.5 : 0.5;
    node.connect(pan).connect(this.master);
    // Tag the pod-side detune base on the node for later beat-detune assignment.
    (node as unknown as { __side: string }).__side = side;
    return { node, pan };
  }

  isStarted(): boolean { return this.started; }

  async resume(): Promise<void> {
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') await this.ctx.resume();
    this.suspended = false;
  }

  async suspend(): Promise<void> {
    if (!this.ctx) return;
    await this.ctx.suspend();
    this.suspended = true;
  }

  isSuspended(): boolean { return this.suspended; }

  dispose(): void {
    this.silence();
    for (const pod of this.pods) {
      try {
        pod.node.port.close();
      } catch {
        // Port may already be closed.
      }
      disconnectNode(pod.node);
      disconnectNode(pod.pan);
    }
    if (this.master) disconnectNode(this.master);
    if (this.comp) disconnectNode(this.comp);
    this.pods = [];
    this.master = null;
    this.comp = null;
    this.ctx = null;
    this.started = false;
    this.suspended = false;
    this.smoothedRpm = 0;
    this.smoothedThrottle = 0;
    this.smoothedLoad = 0;
    this.smoothedSlip = 0;
    this.smoothedShift = 0;
    this.releaseHiss = 0;
    this.lastThrottleInput = 0;
    this.lastDrive = 0;
  }

  // Apply a full preset snapshot. Cheap — sets all AudioParams to current value.
  applyPreset(preset: AudioPreset): void {
    this.preset = clonePreset(preset);
    if (!this.started) return;
    if (this.master) this.master.gain.value = 1;
    for (const pod of this.pods) {
      this.applyPodConfig(pod);
    }
    this.applyTwin();
  }

  private applyPodConfig(pod: PodGraph): void {
    const p = this.preset;
    const params = pod.node.parameters;
    setK(params, 'mixCylinder', p.mix.cylinder);
    setK(params, 'mixIntake', p.mix.intake);
    setK(params, 'mixExhaust', p.mix.exhaust);
    setK(params, 'mixBlock', p.mix.block);
    setK(params, 'mixSlip', p.mix.slip);
    setK(params, 'mixDrone', p.mix.drone);
    setK(params, 'mixWhine', p.mix.whine);
    setK(params, 'mixHiss', p.mix.hiss);
    setK(params, 'mixRoar', p.mix.roar);
    setK(params, 'mixRacecar', p.mix.racecar ?? 0);
    setK(params, 'mixProp', p.mix.prop ?? 0);
    setK(params, 'mixMotor', p.mix.motor ?? 0);
    setK(params, 'mixShift', p.mix.shift ?? 0);
    setK(params, 'mixComb', p.mix.comb ?? 0);
    setK(params, 'roarBaseHz', p.pod.roarBaseHz);
    setK(params, 'roarSweepHz', p.pod.roarSweepHz);
    setK(params, 'roarBandwidth', p.pod.roarBandwidth);
    setK(params, 'roarFloor', p.pod.roarFloor);
    setK(params, 'roarCurve', p.pod.roarCurve);
    setK(params, 'pulseFade', p.pod.pulseFade);
    setK(params, 'racecarRatio', p.pod.racecarRatio ?? 3.5);
    setK(params, 'racecarFormantHz', p.pod.racecarFormantHz ?? 760);
    setK(params, 'racecarGrit', p.pod.racecarGrit ?? 0.4);
    setK(params, 'propRate', p.pod.propRate ?? 2.5);
    setK(params, 'propDepth', p.pod.propDepth ?? 0.45);
    setK(params, 'motorBuzzRatio', p.pod.motorBuzzRatio ?? 24);
    setK(params, 'motorBuzzDetune', p.pod.motorBuzzDetune ?? 0.15);
    setK(params, 'heartbeatDepth', p.pod.heartbeatDepth ?? 0.1);
    setK(params, 'heartbeatRateHz', p.pod.heartbeatRateHz ?? 1.4);
    setK(params, 'combDelayMs', p.pod.combDelayMs ?? 5.9);
    setK(params, 'combFeedback', p.pod.combFeedback ?? 0.68);
    setK(params, 'combDamping', p.pod.combDamping ?? 0.32);
    setK(params, 'combDrive', p.pod.combDrive ?? 0.58);
    setK(params, 'masterGain', p.mix.master);
    setK(params, 'feedback', p.pod.feedback);
    setK(params, 'damping', p.pod.damping);
    setK(params, 'pulseWidth', p.pod.pulseWidth);
    setK(params, 'pulseGrit', p.pod.pulseGrit);
    setK(params, 'firingJitter', p.pod.firingJitter);
    setK(params, 'wobbleDepth', p.pod.wobbleDepth);
    setK(params, 'wobbleRateHz', p.pod.wobbleRateHz);
    setK(params, 'droneRatio', p.pod.droneRatio);
    setK(params, 'droneCutoff', p.pod.droneCutoff);
    setK(params, 'whineRatio', p.pod.whineRatio);
    setK(params, 'whineThreshold', p.pod.whineThreshold);
    pod.node.port.postMessage({
      cylinders: p.pod.cylinders,
      unevenFiring: p.pod.unevenFiring,
      intakeCm: p.pod.intakeCm,
      exhaustCm: p.pod.exhaustCm,
    });
  }

  private applyTwin(): void {
    if (this.pods.length < 2) return;
    const w = Math.max(0, Math.min(1, this.preset.twin.stereoWidth));
    this.pods[0].pan.pan.value = -w;
    this.pods[1].pan.pan.value = w;
  }

  // Single-knob updates from lil-gui — the panel mutates `currentPreset()` then
  // calls applyPreset() again. For per-frame updates use `update()`.
  currentPreset(): AudioPreset { return this.preset; }

  // Per-frame physics → audio mapping. Called from the game/tuner update loop.
  update(s: PhysicsSignals, dt: number): void {
    if (!this.started || !this.ctx) return;
    const t = this.ctx.currentTime;

    const p = this.preset;
    // Map throttle+speed → RPM with the configured curve.
    const drive = (1 - p.throttle.speedWeight) * s.throttle + p.throttle.speedWeight * s.speed;
    const curved = Math.pow(Math.max(0, Math.min(1, drive)), p.throttle.rpmCurve);
    const rpmTarget = p.throttle.rpmIdle + (p.throttle.rpmRedline - p.throttle.rpmIdle) * curved;
    const driveChange = Math.abs(drive - this.lastDrive) / Math.max(0.016, dt);
    const shiftTarget = Math.max(0, Math.min(1, (driveChange - 0.75) * 0.12)) * (0.25 + s.speed * 0.75);
    this.smoothedShift = Math.max(this.smoothedShift * Math.exp(-dt * 7.5), shiftTarget);
    this.lastDrive = drive;

    const releaseHit = releaseHissHitForThrottleDrop(this.lastThrottleInput, s.throttle, s.speed, Math.max(s.load, this.smoothedLoad));
    this.releaseHiss = s.throttle > this.lastThrottleInput + 0.01
      ? 0
      : releaseHissEnvelope(this.releaseHiss, releaseHit, dt);
    this.lastThrottleInput = s.throttle;

    // Smoothing — audio responds slightly faster than physics for snappy feel.
    const rpmSmooth = 1 - Math.exp(-dt * 12);
    const inputSmooth = 1 - Math.exp(-dt * 18);
    this.smoothedRpm += (rpmTarget - this.smoothedRpm) * rpmSmooth;
    this.smoothedThrottle += (s.throttle - this.smoothedThrottle) * inputSmooth;
    this.smoothedLoad += (s.load - this.smoothedLoad) * inputSmooth;
    this.smoothedSlip += (s.slip - this.smoothedSlip) * inputSmooth;

    const beat = p.twin.beatDetuneHz / 2;
    // The two pods straddle the target rpm by ±beat/2 so they form audible
    // amplitude beating at the difference frequency. podSync pulls them back
    // toward the same rpm — at sync=1 the throb disappears.
    const sync = Math.max(0, Math.min(1, p.twin.podSync));
    const splitL = -beat * (1 - sync);
    const splitR = +beat * (1 - sync);

    // Hiss gate: a release transient, not a constant coasting layer.
    const hissGate = this.releaseHiss;

    for (let i = 0; i < this.pods.length; i++) {
      const pod = this.pods[i];
      const split = i === 0 ? splitL : splitR;
      const rpm = Math.max(0.5, this.smoothedRpm + split);
      const params = pod.node.parameters;
      const rpmParam = params.get('rpmHz');
      if (rpmParam) rpmParam.setTargetAtTime(rpm, t, 0.05);
      const thr = params.get('throttle');
      if (thr) thr.setTargetAtTime(this.smoothedThrottle, t, 0.05);
      const ld = params.get('load');
      if (ld) ld.setTargetAtTime(this.smoothedLoad, t, 0.05);
      const slip = params.get('slip');
      if (slip) slip.setTargetAtTime(this.smoothedSlip, t, 0.05);
      const detune = params.get('detuneCents');
      if (detune) detune.setTargetAtTime(p.pod.detuneCents, t, 0.1);
      const hg = params.get('hissGate');
      if (hg) hg.setTargetAtTime(hissGate, t, 0.04);
      const shift = params.get('shift');
      if (shift) shift.setTargetAtTime(this.smoothedShift, t, 0.025);
    }
  }

  silence(): void {
    if (!this.started || !this.ctx) return;
    this.smoothedRpm = 0;
    this.smoothedThrottle = 0;
    this.smoothedLoad = 0;
    this.smoothedSlip = 0;
    this.smoothedShift = 0;
    this.releaseHiss = 0;
    this.lastThrottleInput = 0;
    this.lastDrive = 0;
    const t = this.ctx.currentTime;
    for (const pod of this.pods) {
      const params = pod.node.parameters;
      params.get('rpmHz')?.setTargetAtTime(0.5, t, 0.02);
      params.get('throttle')?.setTargetAtTime(0, t, 0.02);
      params.get('load')?.setTargetAtTime(0, t, 0.02);
      params.get('slip')?.setTargetAtTime(0, t, 0.02);
      params.get('hissGate')?.setTargetAtTime(0, t, 0.02);
      params.get('shift')?.setTargetAtTime(0, t, 0.02);
    }
  }

  // Live single-param refresh from the lil-gui panel.
  refreshFromPreset(): void { this.applyPreset(this.preset); }

  context(): AudioContext | null { return this.ctx; }
  destinationGain(): GainNode | null { return this.master; }

  diagnostic(): { rpm: number; throttle: number; load: number; slip: number; shift: number } {
    return {
      rpm: this.smoothedRpm,
      throttle: this.smoothedThrottle,
      load: this.smoothedLoad,
      slip: this.smoothedSlip,
      shift: this.smoothedShift,
    };
  }
}

function setK(params: AudioParamMap, name: string, value: number): void {
  const p = params.get(name);
  if (p) p.value = value;
}

function disconnectNode(node: AudioNode): void {
  try {
    node.disconnect();
  } catch {
    // Already disconnected.
  }
}
